import React, { useEffect, useState, useContext, useRef } from "react";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import DeleteIcon from "@material-ui/icons/Delete";
import RemoveCircleOutlineIcon from "@material-ui/icons/RemoveCircleOutline";
import MenuBookIcon from "@material-ui/icons/MenuBook";
import FingerprintIcon from "@material-ui/icons/Fingerprint";
import PublicIcon from "@material-ui/icons/Public";
import LibraryBooksSharpIcon from "@material-ui/icons/LibraryBooksSharp";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import SaveIcon from "@material-ui/icons/Save";
import Tooltip from "@material-ui/core/Tooltip";
import { makeStyles } from "@material-ui/styles";
import { AppContext, DispatchContext } from "../../AppContext";
import * as actions from "../../actions/actions";
import Service from "../../services/service";
import FileUploadPanel from "../../components/FileUploadPanel";
import DividerLabel from "../../components/DividerLabel";

const useStyles = makeStyles(() => ({
    root: {
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        padding: 16,
        gap: 16,
    },
    section: {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 50%",
        minHeight: 0,
        overflow: "hidden",
    },
    sectionTitle: {
        fontWeight: 600,
        fontSize: "0.85rem",
        color: "#1982DD",
        marginBottom: 8,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
    },
    tableWrap: {
        flex: "1 1 auto",
        overflowY: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: 4,
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    th: {
        backgroundColor: "#f5f5f5",
        padding: "6px 10px",
        textAlign: "left",
        borderBottom: "2px solid #e0e0e0",
        fontWeight: 600,
        fontSize: "0.8rem",
        position: "sticky",
        top: 0,
        zIndex: 1,
    },
    td: {
        padding: "5px 10px",
        borderTop: "1px solid #f0f0f0",
        fontSize: "0.85rem",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 0,
    },
    tdAction: {
        padding: "2px 4px",
        borderTop: "1px solid #f0f0f0",
        width: 36,
        textAlign: "center",
    },
    tdType: {
        padding: "2px 8px",
        borderTop: "1px solid #f0f0f0",
        width: 32,
        textAlign: "center",
        color: "#aaa",
    },
    words: {
        color: "#888",
        fontSize: "0.8rem",
        width: 90,
        textAlign: "right",
    },
    emptyRow: {
        padding: "16px 10px",
        color: "#aaa",
        fontStyle: "italic",
        textAlign: "center",
    },
    addBar: {
        marginTop: 8,
        display: "flex",
        justifyContent: "flex-end",
    },
    projectMsg: {
        padding: 16,
        border: "1px solid #e0e0e0",
        borderRadius: 4,
        backgroundColor: "#f9f9f9",
        color: "#555",
        lineHeight: 1.6,
        fontSize: "0.9rem",
    },
}));

export default function ComparisonEditPanel({ reportId, projectId, projectName, reportName, onChanged, onNameChanged, onClose }) {
    const classes = useStyles();
    const state = useContext(AppContext);
    const dispatch = useContext(DispatchContext);
    const tc = (state.strings || {}).textComparisons || {};

    const [name, setName] = useState(reportName || "");
    const savedNameRef = useRef(reportName || "");

    useEffect(() => {
        setName(reportName || "");
        savedNameRef.current = reportName || "";
    }, [reportName]);

    const [texts, setTexts] = useState(null);
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(null); // token being deleted
    const [submitting, setSubmitting] = useState(false);

    const okFiles = (state.uploadedFilesStatus || []).filter(f => f.Status === "Ok");

    const load = () => {
        setLoading(true);
        Service.getComparisonTexts(reportId).then(res => {
            if (res.success) setTexts(res.data.texts || []);
            setLoading(false);
        });
    };

    useEffect(() => {
        if (!reportId || projectId) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportId]);

    const handleNameBlur = () => {
        const trimmed = name.trim();
        if (trimmed === savedNameRef.current) return;
        savedNameRef.current = trimmed;
        Service.UpdateReports({ title: trimmed, field: 'title' }, [reportId], null).then(res => {
            if (res.success) {
                if (onNameChanged) onNameChanged(trimmed);
                if (onChanged) onChanged();
            } else {
                dispatch(actions.openToast({ type: "error", message: (res.errors || ["Error"]).join(", "), status: true }));
            }
        });
    };

    const handleDelete = (token, name, type) => {
        const isComptext = type === 'comptext';
        dispatch(actions.openConfirmationDialog({
            title: isComptext
                ? (tc.removeTextTitle || "Remove text")
                : (tc.removeTextTitle || "Remove text"),
            text: tc.removeTextText ? tc.removeTextText.format(name) : `Remove "${name}" from this comparison?`,
            hint: isComptext
                ? (tc.removeTextHintComptext || "This uploaded text will be permanently deleted along with all comparison results involving it.")
                : (tc.removeTextHintLibtext || "The library text itself will not be deleted \u2014 it will only be removed from this comparison. The associated results will be deleted."),
            okLabel: tc.removeTextOk || "Remove",
            onConfirm: () => {
                setDeleting(token);
                Service.deleteComparisonText(reportId, token).then(res => {
                    setDeleting(null);
                    dispatch(actions.closeConfirmationDialog());
                    if (res.success) {
                        load();
                        if (onChanged) onChanged();
                    } else {
                        dispatch(actions.openToast({ type: "error", message: (res.errors || ["Error"]).join(", "), status: true }));
                    }
                });
            },
        }));
    };

    const handleAddUploads = () => {
        const uploadids = okFiles.map(f => f.UploadId);
        if (!uploadids.length) return;
        setSubmitting(true);
        Service.addComparisonUploads(reportId, uploadids).then(res => {
            setSubmitting(false);
            if (res.success) {
                dispatch(actions.cancelFilesUpload());
                dispatch(actions.setUploadedFilesStatus({ Files: [] }));
                load();
                if (onChanged) onChanged();
            } else {
                dispatch(actions.openToast({ type: "error", message: (res.errors || ["Error"]).join(", "), status: true }));
            }
        });
    };

    if (projectId) {
        return (
            <div className={classes.root}>
                <div className={classes.projectMsg}>
                    {tc.syncedComparisonText
                        ? tc.syncedComparisonText.format(projectName)
                        : <>This text comparison is synchronized with the project <strong>&#34;{projectName}&#34;</strong>.</>}<br />
                    {tc.syncedComparisonHint || "To change the texts in the comparison, add library texts to or remove them from the project."}
                </div>
            </div>
        );
    }

    return (
        <div className={classes.root}>
            {/* Comparison name */}
            <TextField
                label={tc.comparisonName || "Name of Text Comparison"}
                placeholder={tc.comparisonNamePlaceholder || "Optional"}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={handleNameBlur}
                variant="outlined"
                size="small"
                fullWidth
                style={{ flexShrink: 0 }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                size="small"
                                edge="end"
                                disabled={name.trim() === savedNameRef.current}
                                onClick={handleNameBlur}
                            >
                                <SaveIcon fontSize="small" />
                            </IconButton>
                        </InputAdornment>
                    )
                }}
            />
            {/* Current texts */}
            <div className={classes.section}>
                <DividerLabel label={tc.textsInComparison || "Texts in this comparison"} />
                <div className={classes.tableWrap}>
                    {loading ? (
                        <div style={{ padding: 24, textAlign: "center" }}><CircularProgress size={24} /></div>
                    ) : (
                        <table className={classes.table}>
                            <thead>
                                <tr>
                                    <th className={classes.th} style={{ width: 32 }}>{tc.type || "Type"}</th>
                                    <th className={classes.th}>{tc.name || "Name"}</th>
                                    <th className={`${classes.th} ${classes.words}`}>{tc.words || "Words"}</th>
                                    <th className={classes.th} style={{ width: 36 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {texts && texts.length === 0 && (
                                    <tr><td colSpan={4} className={classes.emptyRow}>{tc.noTexts || "No texts"}</td></tr>
                                )}
                                {(texts || []).map(t => (
                                    <tr key={t.token}>
                                        <td className={classes.tdType}>
                                            {t.type === 'comptext'
                                                ? <Tooltip title={tc.typeUpload || "Upload"}><CloudUploadIcon style={{ fontSize: 16, color: "#000066" }} /></Tooltip>
                                                : t.fingerprint
                                                    ? <Tooltip title={tc.typeFingerprint || "Fingerprint"}><FingerprintIcon style={{ fontSize: 16, color: "#000066" }} /></Tooltip>
                                                    : <Tooltip title={tc.typeLibrary || "Library"}><LibraryBooksSharpIcon style={{ fontSize: 16, color: "#000066" }} /></Tooltip>}
                                        </td>
                                        <td className={classes.td}>{t.name}</td>
                                        <td className={`${classes.td} ${classes.words}`}>{t.words.toLocaleString()}</td>
                                        <td className={classes.tdAction}>
                                            <Tooltip title={t.type === 'comptext'
                                                ? (tc.deleteText || "Delete text permanently")
                                                : (tc.removeFromComparison || "Remove from comparison")}>
                                                <span>
                                                    <IconButton
                                                        size="small"
                                                        disabled={deleting === t.token || (texts && texts.length <= 2)}
                                                        onClick={() => handleDelete(t.token, t.name, t.type)}
                                                    >
                                                        {deleting === t.token
                                                            ? <CircularProgress size={16} />
                                                            : t.type === 'comptext'
                                                                ? <DeleteIcon fontSize="small" />
                                                                : <RemoveCircleOutlineIcon fontSize="small" />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Upload new texts */}
            <div className={classes.section}>
                <DividerLabel label={tc.uploadNewText || "Upload new text"} />
                <div style={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
                    <FileUploadPanel showEdit={false} />
                </div>
                <div className={classes.addBar}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={onClose}
                        style={{ marginRight: 8 }}
                    >
                        {tc.cancel || "Cancel"}
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        disabled={okFiles.length === 0 || submitting}
                        onClick={handleAddUploads}
                    >
                        {submitting
                            ? <CircularProgress size={16} style={{ marginRight: 6 }} />
                            : null}
                        {okFiles.length > 0
                            ? (tc.addFilesButton
                                ? tc.addFilesButton.format(okFiles.length, okFiles.length !== 1 ? "s" : "")
                                : `Add ${okFiles.length} file${okFiles.length !== 1 ? "s" : ""} to comparison`)
                            : (tc.addToComparison || "Add to comparison")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
